import { DataType, ICTTextParagraph, IDrawingShapeData, IGroupShapeData, ISlideData, IGraphicFrameBaseData } from '@tencent/slide-sdk-model';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import _unset from 'lodash/unset';
import _get from 'lodash/get';
import { ICTTextBody } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

export class SlideSdkDataModify {
    private uiDataService?: IUiDataService;
    private selectionService?: ISelectionService;

    public setServiceDelegates(services: { uiDataService?: IUiDataService, selectionService?: ISelectionService }) {
        this.uiDataService = this.uiDataService ?? services.uiDataService;
        this.selectionService = this.selectionService ?? services.selectionService;
    }

    /**
     *
     * 从外部文档向内部文档拷贝slide时，需要对slide进行相应的处理：如缩放、layoutid、去除placeholder等逻辑
     * @static
     * @memberof CPUtils
     */
    public adjustPageData = (
        slideSDKData: ISlideData,
        slideSize: {
            width: number,
            height: number,
        },
        notChangeLayout?: boolean,
    ) => {
        if (!notChangeLayout) {
            // 处理layoutId
            const layout = this.getLayout();
            slideSDKData.layoutId = layout.id;
        }

        // this.checkPlaceHolder(slideSDKData);

        // 调整缩放
        if (slideSize) {
            // 传入的宽高
            const outWidth = slideSize.width;
            const outHeight = slideSize.height;
            const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation();
            // 当前的宽高
            const currentWidth = presentation?.props.sldSz.cx || 0;
            const currentHeight = presentation?.props.sldSz.cy || 0;

            let ratio = 0; // 缩放比例
            let offsetTop = 0; // 居中处理的偏移量
            let offsetLeft = 0;
            const ratioH = currentHeight / outHeight;
            // 计算缩放比
            if ((currentWidth / outWidth) <= (currentHeight / outHeight)) {
                ratio = currentWidth / outWidth;
                const targetHeight = outHeight * ratio;
                offsetTop = (currentHeight - targetHeight) / 2;
            } else {
                ratio = currentHeight / outHeight;
                const targetWidth = outWidth * ratio;
                offsetLeft = (currentWidth - targetWidth) / 2;
            }

            if (slideSDKData?.cSld?.spTree?.shapeList) {
                slideSDKData?.cSld?.spTree?.shapeList.forEach((element) => {
                    // 处理通用缩放（frame）
                    if (element.spPr.xfrm?.off?.x) {
                        element.spPr.xfrm.off.x = (element.spPr.xfrm?.off?.x * ratio) + offsetLeft;
                    }
                    if (element.spPr.xfrm?.off?.y) {
                        element.spPr.xfrm.off.y = (element.spPr.xfrm?.off?.y * ratioH) + offsetTop;
                    }
                    if (element.spPr.xfrm?.ext?.cx) {
                        element.spPr.xfrm.ext.cx *= ratio;
                    }
                    if (element.spPr.xfrm?.ext?.cy) {
                        element.spPr.xfrm.ext.cy *= ratio;
                    }
                    this.adaptElement(element, ratio, ratioH);
                });
            }
        }
    };

    // eslint-disable-next-line complexity
    private getLayout() {
        let layout;
        if (this.checkStateForLayout()) {
            [layout] = this.uiDataService!.presentation.masterList[0].layoutList;
        } else {
            try {
                const curSlide = this.selectionService?.currentSlide;
                let masterIndex = -1;
                const { masterList } = this.uiDataService?.presentation ?? { masterList: [] };
                for (let i = 0; i < masterList.length; i++) {
                    if (curSlide?.sdkData?.getMaster()?.id === masterList[i].id) {
                        masterIndex = i;
                        break;
                    }
                }
                if (masterIndex === -1) {
                    masterIndex = 0;
                }
                let layoutIndex = -1;
                const { layoutList } = this.uiDataService?.presentation.masterList[masterIndex] ?? { layoutList: [] };
                for (let i = 0; i < layoutList.length; i++) {
                    // @ts-ignore
                    if (curSlide?.sdkData?.props?.layoutId === layoutList[i].id) {
                        layoutIndex = i;
                        break;
                    }
                }
                if (layoutIndex === -1) {
                    layoutIndex = 0;
                }
                layout = this.uiDataService?.presentation.masterList[masterIndex].layoutList[layoutIndex];
            } catch (error) {
                if ((this.uiDataService?.presentation.masterList.length ?? 0) > 0) {
                    [layout] = this.uiDataService!.presentation.masterList[0].layoutList;
                } else {
                    layout = { id: 0 };
                }
            }
        }
        return layout;
    }

    private checkStateForLayout() {
        return this.selectionService?.slideClearanceIndex === 0
            || (this.selectionService?.slideClearanceIndex === -1 && this.selectionService?.currentSlideIndex === -1);
    }

    private checkPlaceHolder(slideSDKData) {
        // 处理placeHolder
        const unsetPlaceHolder = (models) => {
            models.forEach((model) => {
                if (model.type === 'shape' && model.isPlaceholder) {
                    _unset(model.props, 'placeholder');
                }
            });
        };
        const sd = slideSDKData.slideData;
        if (_get(sd, 'groupShape.children.length') > 0) {
            unsetPlaceHolder(sd.groupShape.children);
        }
    }

    private adaptElement(element: IDrawingShapeData, ratio: number, ratioH: number) {
        if (element.type === DataType.GroupShape) {
            this.adaptInnerGroupShape(element, ratio);
        }

        if (element.type === DataType.Shape && element.txBody?.p) {
            this.adaptTextBody(element.txBody, ratio);
        }

        if (element.type === DataType.GraphicalObjectFrameTable) {
            this.adaptTable(element, ratio, ratioH);
        }
    }


    // 缩放组内部的文字
    private adaptInnerGroupShape(groupShape: IGroupShapeData, ratio: number) {
        if (groupShape.shapeList?.length) {
            groupShape.shapeList.forEach((element) => {
                if (element.type === DataType.GroupShape) {
                    // 递归处理成组
                    this.adaptInnerGroupShape(element, ratio);
                }
                if (element.type === DataType.Shape && element.txBody?.p) {
                    this.adaptTextBody(element.txBody, ratio);
                }
            });
        }
    }

    // 缩放table及内部元素
    private adaptTable(tableData: IGraphicFrameBaseData, ratio, ratioH) {
        if (tableData.graphic?.graphicData?.tbl) {
            tableData.graphic?.graphicData?.tbl.tblGrid?.gridCol?.forEach((tblCol) => {
                if (tblCol.w) {
                    tblCol.w *= ratio;
                }
            });
            tableData.graphic?.graphicData?.tbl.tr?.forEach((tblRow) => {
                if (tblRow.h) {
                    tblRow.h *= ratio;
                }
                tblRow.tc?.forEach((tc) => {
                    if (tc.txBody) {
                        this.adaptTextBody(tc.txBody, ratio);
                    }
                });
            });
        }
    }

    // 缩放文本框内部
    private adaptTextBody(textBody: ICTTextBody, ratio: number) {
        if (textBody?.p) {
            textBody.p.forEach((paragraph: ICTTextParagraph) => {
                paragraph.egTextRun?.forEach((textRun) => {
                    const textCharacterProperties = (textRun.fld || textRun.r)?.rPr;
                    if (textCharacterProperties?.sz) {
                        textCharacterProperties.sz *= ratio;
                    }
                });
                if (paragraph.pPr) {
                    if (paragraph.pPr.indent) {
                        paragraph.pPr.indent *= ratio;
                    }
                    if (paragraph.pPr.marL) {
                        paragraph.pPr.marL *= ratio;
                    }
                    if (paragraph.pPr.lnSpc?.spcPts?.val) {
                        paragraph.pPr.lnSpc.spcPts.val *= ratio;
                    }
                    if (paragraph.pPr.spcAft?.spcPts?.val) {
                        paragraph.pPr.spcAft.spcPts.val *= ratio;
                    }
                    if (paragraph.pPr.spcBef?.spcPts?.val) {
                        paragraph.pPr.spcBef.spcPts.val *= ratio;
                    }
                }
                if (paragraph.endParaRPr?.sz) {
                    paragraph.endParaRPr.sz *= ratio;
                }
            });
        }

        // TODO @muzzy textStyleList的字号修改需要看一下
        // if (_has(textBody, 'textStyleList')) {
        //     textBody.textStyleList.forEach((textStyle) => {
        //         textStyle.defaultRunProps.font_size = textStyle.defaultRunProps.font_size * ratio;
        //     });
        // }

        // 文本框缩进相关
        this.lineIndent(textBody, ratio);
    }

    private lineIndent(textBody: ICTTextBody, ratio: number) {
        if (textBody.bodyPr) {
            if (ratio !== 1) {
                // 看了下 office 的表现，在跨文档粘贴的时候自缩排和根据文字调整形状大小属性并不会删除
                // 缩放比例有变化的话，在原文档中计算的 fontScale 在当前文档中可能是不对的，所以去掉这个值，渲染的时候动态计算
                if (textBody.bodyPr.normAutofit) {
                    _unset(textBody.bodyPr.normAutofit, 'fontScale');
                }
            }
            if (textBody.bodyPr.bIns) {
                textBody.bodyPr.bIns *= ratio;
            }
            if (textBody.bodyPr.lIns) {
                textBody.bodyPr.lIns *= ratio;
            }
            if (textBody.bodyPr.rIns) {
                textBody.bodyPr.rIns *= ratio;
            }
            if (textBody.bodyPr.tIns) {
                textBody.bodyPr.tIns *= ratio;
            }
        }
    }
}
